import React, { useState, useEffect } from 'react';
import { 
  FiPlus, FiFilter, FiSearch, FiTrendingUp, 
  FiUsers, FiDollarSign, FiCalendar, FiBarChart2,
  FiEdit, FiTrash2, FiPlay, FiPause, FiEye
} from 'react-icons/fi';
import SocialMediadashboardSidebar from '../SocialMediadashboardSidebar';
import { useAuth } from '../../../Context/AuthContext';

const Campaign = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    objective: 'awareness',
    platforms: [],
    budget: '',
    startDate: '',
    endDate: '',
    targetAudience: ''
  });

  const { googleUserInfo } = useAuth();

  // Campaign objectives
  const objectives = [
    { value: 'awareness', label: 'Brand Awareness', icon: FiEye },
    { value: 'engagement', label: 'Engagement', icon: FiUsers },
    { value: 'conversions', label: 'Conversions', icon: FiTrendingUp },
    { value: 'traffic', label: 'Website Traffic', icon: FiBarChart2 }
  ];

  // Platform options
  const platforms = [
    { value: 'facebook', label: 'Facebook', color: 'text-blue-600' },
    { value: 'instagram', label: 'Instagram', color: 'text-pink-600' },
    { value: 'twitter', label: 'Twitter', color: 'text-blue-400' },
    { value: 'linkedin', label: 'LinkedIn', color: 'text-blue-700' }
  ];

  // Load campaigns from localStorage
  useEffect(() => {
    const loadCampaigns = () => {
      try {
        const storedCampaigns = JSON.parse(localStorage.getItem('socialCampaigns') || '[]');
        setCampaigns(storedCampaigns);
        setFilteredCampaigns(storedCampaigns);
      } catch (error) {
        console.error('Error loading campaigns:', error);
        setCampaigns([]);
        setFilteredCampaigns([]);
      } finally {
        setLoading(false);
      }
    };

    loadCampaigns();
  }, []);

  // Filter campaigns
  useEffect(() => {
    let filtered = campaigns;

    if (searchTerm) {
      filtered = filtered.filter(campaign =>
        campaign.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(campaign => campaign.status === statusFilter);
    }

    if (platformFilter !== 'all') {
      filtered = filtered.filter(campaign => 
        campaign.platforms.includes(platformFilter)
      );
    }

    setFilteredCampaigns(filtered);
  }, [campaigns, searchTerm, statusFilter, platformFilter]);

  // Create new campaign
  const createCampaign = () => {
    if (!newCampaign.name || !newCampaign.budget || !newCampaign.startDate) {
      alert('Please fill in all required fields');
      return;
    }

    const campaign = {
      id: Date.now().toString(),
      ...newCampaign,
      status: 'draft',
      createdAt: new Date().toISOString(),
      metrics: {
        spend: 0,
        impressions: 0,
        clicks: 0,
        engagements: 0,
        conversions: 0,
        ctr: 0,
        cpc: 0
      }
    };

    const updatedCampaigns = [...campaigns, campaign];
    setCampaigns(updatedCampaigns);
    localStorage.setItem('socialCampaigns', JSON.stringify(updatedCampaigns));
    setShowCreateModal(false);
    setNewCampaign({
      name: '',
      objective: 'awareness',
      platforms: [],
      budget: '',
      startDate: '',
      endDate: '',
      targetAudience: ''
    });
  };

  // Update campaign status
  const updateCampaignStatus = (campaignId, status) => {
    const updatedCampaigns = campaigns.map(campaign =>
      campaign.id === campaignId ? { ...campaign, status } : campaign
    );
    setCampaigns(updatedCampaigns);
    localStorage.setItem('socialCampaigns', JSON.stringify(updatedCampaigns));
  };

  // Delete campaign
  const deleteCampaign = (campaignId) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      const updatedCampaigns = campaigns.filter(campaign => campaign.id !== campaignId);
      setCampaigns(updatedCampaigns);
      localStorage.setItem('socialCampaigns', JSON.stringify(updatedCampaigns));
    }
  };

  // Toggle platform selection
  const togglePlatform = (platform) => {
    setNewCampaign(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Calculate campaign progress
  const calculateProgress = (campaign) => {
    if (campaign.status === 'completed') return 100;
    if (campaign.status === 'draft') return 0;
    
    const start = new Date(campaign.startDate);
    const end = new Date(campaign.endDate);
    const now = new Date();
    
    if (now < start) return 0;
    if (now > end) return 100;
    
    const total = end - start;
    const elapsed = now - start;
    return Math.min(100, Math.round((elapsed / total) * 100));
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FD5F20] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <SocialMediadashboardSidebar />
      
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <header className="bg-white shadow-sm p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
              <p className="text-gray-600">Manage your social media campaigns</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center bg-[#FD5F20] text-white px-4 py-2 rounded-lg hover:bg-[#e55a1d]"
            >
              <FiPlus className="mr-2" />
              Create Campaign
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mt-6">
            <div className="flex-1 min-w-[300px]">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                />
              </div>
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
            </select>

            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
            >
              <option value="all">All Platforms</option>
              {platforms.map(platform => (
                <option key={platform.value} value={platform.value}>
                  {platform.label}
                </option>
              ))}
            </select>
          </div>
        </header>

        {/* Campaigns Grid */}
        <main className="p-6">
          {filteredCampaigns.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <FiTrendingUp className="text-gray-400 text-2xl" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Campaigns Found</h3>
              <p className="text-gray-600 mb-6">
                {campaigns.length === 0 
                  ? 'Create your first campaign to get started'
                  : 'No campaigns match your current filters'
                }
              </p>
              <button 
                onClick={() => setShowCreateModal(true)}
                className="bg-[#FD5F20] text-white px-4 py-2 rounded-lg hover:bg-[#e55a1d]"
              >
                Create Campaign
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredCampaigns.map(campaign => (
                <div key={campaign.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Campaign Header */}
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-lg text-gray-900">{campaign.name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                        {campaign.status}
                      </span>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600 mb-3">
                      <FiCalendar className="mr-1" />
                      <span>
                        {new Date(campaign.startDate).toLocaleDateString()} - {new Date(campaign.endDate).toLocaleDateString()}
                      </span>
                    </div>

                    {/* Platforms */}
                    <div className="flex flex-wrap gap-2">
                      {campaign.platforms.map(platform => {
                        const platformInfo = platforms.find(p => p.value === platform);
                        return (
                          <span key={platform} className={`text-xs px-2 py-1 rounded ${platformInfo?.color} bg-gray-50`}>
                            {platformInfo?.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="px-6 py-3 bg-gray-50">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{calculateProgress(campaign)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-[#FD5F20] h-2 rounded-full transition-all duration-300"
                        style={{ width: `${calculateProgress(campaign)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Budget</p>
                        <p className="font-semibold">${campaign.budget}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Spend</p>
                        <p className="font-semibold">${campaign.metrics.spend}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Impressions</p>
                        <p className="font-semibold">{campaign.metrics.impressions.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">CTR</p>
                        <p className="font-semibold">{campaign.metrics.ctr}%</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-between mt-4 pt-4 border-t border-gray-200">
                      <div className="flex space-x-2">
                        {campaign.status === 'draft' && (
                          <button
                            onClick={() => updateCampaignStatus(campaign.id, 'active')}
                            className="flex items-center text-green-600 hover:text-green-700 text-sm"
                          >
                            <FiPlay className="mr-1" />
                            Start
                          </button>
                        )}
                        {campaign.status === 'active' && (
                          <button
                            onClick={() => updateCampaignStatus(campaign.id, 'paused')}
                            className="flex items-center text-yellow-600 hover:text-yellow-700 text-sm"
                          >
                            <FiPause className="mr-1" />
                            Pause
                          </button>
                        )}
                        <button className="flex items-center text-blue-600 hover:text-blue-700 text-sm">
                          <FiEye className="mr-1" />
                          View
                        </button>
                      </div>
                      <div className="flex space-x-2">
                        <button className="text-gray-400 hover:text-gray-600">
                          <FiEdit />
                        </button>
                        <button 
                          onClick={() => deleteCampaign(campaign.id)}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold">Create New Campaign</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Name *
                </label>
                <input
                  type="text"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                  placeholder="Enter campaign name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Objective
                </label>
                <select
                  value={newCampaign.objective}
                  onChange={(e) => setNewCampaign({...newCampaign, objective: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                >
                  {objectives.map(obj => (
                    <option key={obj.value} value={obj.value}>{obj.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Platforms
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {platforms.map(platform => (
                    <button
                      key={platform.value}
                      type="button"
                      onClick={() => togglePlatform(platform.value)}
                      className={`flex items-center p-3 border rounded-lg text-sm ${
                        newCampaign.platforms.includes(platform.value)
                          ? 'border-[#FD5F20] bg-orange-50 text-[#FD5F20]'
                          : 'border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      <span className={platform.color}>{platform.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budget ($) *
                </label>
                <input
                  type="number"
                  value={newCampaign.budget}
                  onChange={(e) => setNewCampaign({...newCampaign, budget: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                  placeholder="Enter budget"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={newCampaign.startDate}
                    onChange={(e) => setNewCampaign({...newCampaign, startDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={newCampaign.endDate}
                    onChange={(e) => setNewCampaign({...newCampaign, endDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Audience
                </label>
                <input
                  type="text"
                  value={newCampaign.targetAudience}
                  onChange={(e) => setNewCampaign({...newCampaign, targetAudience: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                  placeholder="Describe your target audience"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={createCampaign}
                className="px-4 py-2 bg-[#FD5F20] text-white rounded-lg hover:bg-[#e55a1d]"
              >
                Create Campaign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaign;